import React from 'react';
import { Pane, Card, Heading, Text, Strong, Spinner, InfoSignIcon, Tooltip, Alert } from 'evergreen-ui';
import { Line, Bar } from 'react-chartjs-2';
import { formatCurrency, formatNumber } from '../../../util';
import RevenueFormatTable from './RevenueFormatTable';
import ExpenseCategoryTable from './ExpenseCategoryTable';
import StatsCard from './StatsCard';
import FinancialSummaryCard from './FinancialSummaryCard';
import RevenueByRegionTable from './RevenueByRegionTable';
import RevenueByRegionPieChart from './RevenueByRegionPieChart';
import { useSelector } from 'react-redux';

const DashboardReport = ({
    timeSeriesChartData,
    timeSeriesChartOptions,
    formattedDashboardData,
    currentpublisher,
    getProfitLoss,
    startDate,
    endDate
}) => {
    // Get the publisher's currency from Redux store
    const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
    console.log('Publisher Global Data from Redux:', publisherGlobalData);
    const currencySymbol = publisherGlobalData.currency?.symbol || '$';
    const enableSalesByRegion = publisherGlobalData.enableSalesByRegion || false;

    // Add debug logging
    React.useEffect(() => {
        console.log('Chart Data:', timeSeriesChartData);
        console.log('Chart Options:', timeSeriesChartOptions);
        console.log('Dashboard Data:', formattedDashboardData);
        console.log('Date Range:', { startDate, endDate });
        console.log('Currency Symbol:', currencySymbol);
        console.log('Enable Sales By Region:', enableSalesByRegion);
    }, [timeSeriesChartData, timeSeriesChartOptions, formattedDashboardData, startDate, endDate, currencySymbol, enableSalesByRegion]);

    // Helper function to format currency with the publisher's currency symbol
    const formatCurrencyWithSymbol = (amount) => {
        return formatCurrency(amount, 'USD', currencySymbol);
    };

    // Add validation for dashboard data
    if (!formattedDashboardData) {
        return (
            <Alert
                intent="warning"
                title="No data available"
                marginBottom={16}
            >
                No dashboard data is available for the selected time period.
            </Alert>
        );
    }

    // Validate chart data
    const hasChartData = formattedDashboardData.chart_data &&
                         typeof formattedDashboardData.chart_data.revenue === 'number' &&
                         typeof formattedDashboardData.chart_data.expenses === 'number';

    // Safely get revenue and expenses values
    const revenue = hasChartData ? formattedDashboardData.chart_data.revenue : 0;
    const expenses = hasChartData ? formattedDashboardData.chart_data.expenses : 0;
    const profit = revenue - expenses;

    // Generate chart data with validation
    const chartDataForDashboard = {
        labels: ['Revenue', 'Expenses'],
        datasets: [
            {
                data: [revenue || 0, expenses || 0],
                backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)'],
            },
        ],
    };

    const chartOptionsForDashboard = {
        responsive: true,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    callback: (value) => formatCurrencyWithSymbol(value),
                },
            },
        },
    };

    return (
        <>
            {/* Main dashboard view */}
            <Pane display="flex" gap={24}>
                {/* Left Box - Revenue vs Expenses */}
                <Card flex={1} padding={16} background="tint1" elevation={1}>
                    <Pane display="flex" alignItems="center" marginBottom={16}>
                        <Heading size={600}>Revenue vs Expenses</Heading>
                        <Tooltip
                            content="This chart, and the rest of the data in these reports, tallies revenue according to orders marked as 'paid' against expenses and creator payments. We use the order created date, the expense date, and the royalty sent date for determining time periods."
                            appearance="card"
                        >
                            <InfoSignIcon size={16} marginLeft={8} color="muted" />
                        </Tooltip>
                    </Pane>
                    {!timeSeriesChartData ? (
                        <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
                            <Spinner />
                        </Pane>
                    ) : timeSeriesChartData.labels?.length === 0 ? (
                        <Pane display="flex" alignItems="center" justifyContent="center" height={200}>
                            <Text>No data available for selected period</Text>
                        </Pane>
                    ) : (
                        <Line
                            data={timeSeriesChartData}
                            options={timeSeriesChartOptions}
                            fallbackContent={<Spinner />}
                        />
                    )}
                </Card>

                {/* Right Box - Stats */}
                <StatsCard
                    formattedDashboardData={formattedDashboardData}
                    currentpublisher={currentpublisher}
                    getProfitLoss={getProfitLoss}
                    startDate={startDate}
                    endDate={endDate}
                    formatCurrencyWithSymbol={formatCurrencyWithSymbol}
                    currencySymbol={currencySymbol}
                />
            </Pane>

            {/* Row 2: Revenue by Format | Revenue by Region (if enabled) */}
            <Pane display="flex" gap={24} marginTop={24}>
                <RevenueFormatTable
                    data={formattedDashboardData?.revenue_by_format}
                    formatCurrencyWithSymbol={formatCurrencyWithSymbol}
                    currencySymbol={currencySymbol}
                />
                {enableSalesByRegion && (
                    <Pane display="flex" flexDirection="column" flex={1} gap={24}> {/* This Pane wraps Table and Pie */}
                        <RevenueByRegionTable
                            data={formattedDashboardData?.revenue_by_region}
                            formatCurrencyWithSymbol={formatCurrencyWithSymbol}
                            currencySymbol={currencySymbol}
                        />
                        <RevenueByRegionPieChart
                            data={formattedDashboardData?.revenue_by_region}
                            currencySymbol={currencySymbol}
                        />
                    </Pane>
                )}
                {/* If sales by region is NOT enabled, ExpenseCategoryTable would be here, or we need a placeholder to maintain layout if RevenueFormatTable is alone */}
                {!enableSalesByRegion && <Pane flex={1} />} {/* Placeholder if region is off and we want two columns */}
            </Pane>

            {/* Row 3: Expenses by Category | Financial Summary */}
            <Pane display="flex" marginTop={24} gap={24}>
                <ExpenseCategoryTable
                    data={formattedDashboardData?.expenses_by_category}
                    formatCurrencyWithSymbol={formatCurrencyWithSymbol}
                    currencySymbol={currencySymbol}
                    flex={1} // Make it take up available space
                />
                <FinancialSummaryCard
                    revenueData={formattedDashboardData?.revenue_by_format}
                    expenseData={formattedDashboardData?.expenses_by_category}
                    timePeriod={formattedDashboardData?.time_period}
                    formatCurrencyWithSymbol={formatCurrencyWithSymbol}
                    currencySymbol={currencySymbol}
                    startDate={startDate}
                    endDate={endDate}
                    flex={1} // Make it take up available space
                />
            </Pane>
        </>
    );
};

export default DashboardReport;
